import React, { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Modal, Segmented, Space, Statistic, Steps, Table, Tag, Typography, Upload, notification } from 'antd';
import { CheckCircleOutlined, DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';

/**
 * Bulk load of facility account details (finance): download the template,
 * fill account number / bank name / account name, check the file, apply it.
 *
 * Not the facility bulk upload: this creates nothing and only fills account
 * details on facilities that already exist, matched by facility_id. Every
 * change is audited like an edit on the facility page.
 */
const RESULT_TAGS = {
  updated: { color: 'green', label: 'Updated' },
  would_update: { color: 'blue', label: 'Will update' },
  unchanged: { color: 'default', label: 'Unchanged' },
  skipped: { color: 'gold', label: 'Skipped' },
  failed: { color: 'red', label: 'Failed' },
};

const FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Failed', value: 'failed' },
  { label: 'Skipped', value: 'skipped' },
  { label: 'Updates', value: 'updates' },
];

function reportCsv(rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [['row', 'facility_id', 'facility_name', 'result', 'reason'].join(',')];
  rows.forEach((r) => {
    lines.push([r.row, r.facility_id, r.facility_name, RESULT_TAGS[r.result]?.label || r.result, r.reason].map(esc).join(','));
  });
  return `﻿${lines.join('\n')}\n`;
}

export default function FacilityAccountsImportModal({ open, onClose, onApplied }) {
  const [file, setFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [check, setCheck] = useState(null); // dry-run result
  const [applied, setApplied] = useState(null); // real-run result
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);

  const reset = () => {
    setFile(null);
    setCheck(null);
    setApplied(null);
    setError(null);
    setFilter('all');
  };

  const close = () => {
    reset();
    setOverwrite(false);
    onClose?.();
  };

  const downloadTemplate = async () => {
    try {
      await lib.downloadFacilityAccountTemplateV2();
    } catch (e) {
      notification.error({ message: 'Could not download the template', description: e?.message });
    }
  };

  const run = async (dryRun) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await lib.runFacilityAccountImportV2(file, { dryRun, overwrite });
      if (dryRun) {
        setCheck(result);
        setApplied(null);
      } else {
        setApplied(result);
        onApplied?.(result);
      }
      setFilter(result?.counts?.failed ? 'failed' : 'all');
    } catch (e) {
      setError(e?.message || 'The file could not be processed');
    } finally {
      setBusy(false);
    }
  };

  const result = applied || check;
  const rows = useMemo(() => {
    const all = result?.rows || [];
    if (filter === 'all') return all;
    if (filter === 'updates') return all.filter((r) => r.result === 'updated' || r.result === 'would_update');
    return all.filter((r) => r.result === filter);
  }, [result, filter]);

  const counts = result?.counts || {};
  const toApply = check?.counts?.updated || 0;
  const step = applied ? 2 : check ? 1 : 0;

  const downloadReport = () => {
    const blob = new Blob([reportCsv(result?.rows || [])], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facility-account-import-report${applied ? '' : '-check'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const footer = applied ? (
    <Space>
      <Button onClick={downloadReport}>Download report</Button>
      <Button onClick={reset}>Import another file</Button>
      <Button type="primary" onClick={close}>Done</Button>
    </Space>
  ) : check ? (
    <Space>
      <Button onClick={downloadReport}>Download report</Button>
      <Button onClick={() => { setCheck(null); setFile(null); }}>Choose another file</Button>
      <Button type="primary" disabled={!toApply} loading={busy} onClick={() => run(false)}>
        {toApply ? `Apply ${toApply} update${toApply === 1 ? '' : 's'}` : 'Nothing to apply'}
      </Button>
    </Space>
  ) : (
    <Space>
      <Button onClick={close}>Cancel</Button>
      <Button type="primary" disabled={!file} loading={busy} onClick={() => run(true)}>
        Check file
      </Button>
    </Space>
  );

  return (
    <Modal open={open} title="Import account details" onCancel={close} footer={footer} width={860} destroyOnClose>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Fill in account number, bank name and account name for many facilities at once. This only updates account
        details on existing facilities; it does not add or change facilities. Facilities not in the file are left as
        they are.
      </Typography.Paragraph>

      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 16 }}
        items={[{ title: 'Upload' }, { title: 'Check' }, { title: 'Applied' }]}
      />

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}

      {!check && !applied ? (
        <>
          <ol style={{ paddingLeft: 18, marginBottom: 12 }}>
            <li>
              <Button type="link" icon={<DownloadOutlined />} onClick={downloadTemplate} style={{ padding: 0 }}>
                Download the template
              </Button>{' '}
              — every facility, highest claim volume first.
            </li>
            <li>
              Fill <strong>account_number</strong> (10 digits; format the column as Text so Excel keeps leading zeros),{' '}
              <strong>bank_name</strong> (as on the bank list; GTBank, UBA, Zenith and the like are recognised) and{' '}
              <strong>account_name</strong>. Leave the rest of a row blank to skip it.
            </li>
            <li>Upload it below as .csv or .xlsx and check it. Nothing is saved until you apply.</li>
          </ol>
          <Upload.Dragger
            accept=".csv,.xlsx,.xls"
            maxCount={1}
            beforeUpload={(f) => {
              setFile(f);
              setError(null);
              return false;
            }}
            onRemove={() => setFile(null)}
            fileList={file ? [{ uid: 'f', name: file.name, status: 'done' }] : []}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag the filled file here</p>
            <p className="ant-upload-hint">.csv or .xlsx, up to 5 MB</p>
          </Upload.Dragger>
          <Checkbox checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} style={{ marginTop: 12 }}>
            Replace details on facilities that already have them (otherwise those rows are skipped)
          </Checkbox>
        </>
      ) : (
        <>
          {applied ? (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 12 }}
              message={`${counts.updated || 0} facilit${counts.updated === 1 ? 'y' : 'ies'} updated`}
              description="Each change is recorded in the audit log with the previous value."
            />
          ) : (
            <Alert
              type={counts.failed ? 'warning' : 'info'}
              showIcon
              style={{ marginBottom: 12 }}
              message={`Checked ${check?.file_name || 'the file'}: nothing has been saved yet`}
              description={counts.failed
                ? 'Failed rows will not be saved. Fix them in the file and check again, or apply the valid rows now and import the rest later.'
                : 'Review the rows below, then apply.'}
            />
          )}
          <Space size={32} style={{ marginBottom: 12 }}>
            <Statistic title={applied ? 'Updated' : 'Will update'} value={counts.updated || 0} />
            <Statistic title="Skipped" value={counts.skipped || 0} />
            <Statistic title="Failed" value={counts.failed || 0} valueStyle={counts.failed ? { color: '#cf1322' } : undefined} />
            <Statistic title="Unchanged" value={counts.unchanged || 0} />
            <Statistic title="Blank rows" value={counts.empty || 0} />
          </Space>
          <div style={{ marginBottom: 8 }}>
            <Segmented size="small" options={FILTERS} value={filter} onChange={setFilter} />
          </div>
          <Table
            size="small"
            rowKey={(r) => `${r.row}-${r.facility_id}`}
            dataSource={rows}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            columns={[
              { title: 'Row', dataIndex: 'row', width: 64 },
              { title: 'Facility', dataIndex: 'facility_name', render: (v, r) => v || (r.facility_id ? `#${r.facility_id}` : '—') },
              {
                title: 'Result',
                dataIndex: 'result',
                width: 120,
                render: (v) => <Tag color={RESULT_TAGS[v]?.color}>{RESULT_TAGS[v]?.label || v}</Tag>,
              },
              { title: 'Reason', dataIndex: 'reason', render: (v) => v || '' },
            ]}
          />
        </>
      )}
    </Modal>
  );
}
